import { sessionFetch } from '../../../utils/sessionFetch';
import { COLLA, getServerEndpoint } from '../../../utils/utils';
import { API_KEY } from './constants';

function buildUrl(path, query = {}) {
    const cleanQuery = Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== ''));
    const queryString = new URLSearchParams(cleanQuery).toString();
    const baseUrl = `${getServerEndpoint(COLLA)}/api${path}`;
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

async function parseJsonResponse(response) {
    const contentType = (response.headers && typeof response.headers.get === 'function' && response.headers.get('content-type')) || '';
    if (!contentType.includes('application/json')) {
        return null;
    }
    return response.json();
}

export async function fetchJson(path, { method = 'GET', body, query, responseType = 'json' } = {}) {
    let response;
    try {
        response = await sessionFetch(buildUrl(path, query), {
            method,
            headers: {
                'x-api-key': API_KEY,
                ...(body ? { 'Content-Type': 'application/json' } : {}),
            },
            ...(body ? { body: JSON.stringify(body) } : {}),
        });
    } catch (networkError) {
        const error = new Error('No s\'ha pogut connectar amb el servidor. Comprova la connexió i torna-ho a provar.');
        error.network = true;
        throw error;
    }

    const payload = response.ok && responseType === 'blob' ? null : await parseJsonResponse(response);
    if (!response.ok) {
        const error = new Error(payload?.message || 'Error de xarxa');
        error.status = response.status;
        error.code = payload?.code || null;
        error.payload = payload;
        throw error;
    }

    return responseType === 'blob' ? response.blob() : payload;
}

export function fetchLegalBoardOverview(userId, { refresh = false } = {}) {
    return fetchJson('/legal-board/overview', { query: { userId, refresh: refresh ? '1' : undefined } });
}

export function fetchLegalBoardBadge(userId) {
    return fetchJson('/legal-board/badge', { query: { userId } });
}

export function createLegalBoardTerm({ userId, changeType, cloneFromCurrent = false, alreadyRegistered = false, members, compositionException, requestId }) {
    return fetchJson('/legal-board/terms', {
        method: 'POST',
        body: { userId, changeType, cloneFromCurrent, alreadyRegistered, members, compositionException, requestId },
    });
}

export function updateLegalBoardTerm({ userId, termId, payload }) {
    return fetchJson(`/legal-board/terms/${encodeURIComponent(termId)}`, {
        method: 'PUT',
        body: { userId, ...payload },
    });
}

export function activateLegalBoardTerm({ userId, termId, requestId }) {
    return fetchJson(`/legal-board/terms/${encodeURIComponent(termId)}/activate`, {
        method: 'POST',
        body: { userId, requestId },
    });
}

export function cancelLegalBoardTerm({ userId, termId, requestId }) {
    return fetchJson(`/legal-board/terms/${encodeURIComponent(termId)}/cancel`, {
        method: 'POST',
        body: { userId, requestId },
    });
}

export function applyLegalBoardGencat({ userId, termId, action, date, reference, note, requestId }) {
    return fetchJson(`/legal-board/terms/${encodeURIComponent(termId)}/gencat`, {
        method: 'POST',
        body: { userId, action, date, reference, note, requestId },
    });
}

export function openLegalBoardStripe({ userId, termId, returnPath }) {
    return fetchJson(`/legal-board/terms/${encodeURIComponent(termId)}/stripe/open`, {
        method: 'POST',
        body: { userId, returnPath },
    });
}

export function readCertificatePdf(file) {
    if (!file || !/\.pdf$/i.test(file.name) || file.size === 0) {
        return Promise.reject(new Error('Selecciona un certificat en format PDF.'));
    }
    if (file.size > 10 * 1024 * 1024) {
        return Promise.reject(new Error('El certificat no pot superar els 10 MB.'));
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('No s’ha pogut llegir el fitxer. Torna’l a seleccionar.'));
        reader.onabort = reader.onerror;
        reader.onload = () => resolve({ name: file.name, data: String(reader.result).split(',')[1] });
        reader.readAsDataURL(file);
    });
}

export function uploadLegalBoardCertificateDocument({ userId, termId, file, requestId }) {
    return fetchJson(`/legal-board/terms/${encodeURIComponent(termId)}/certificate-document`, {
        method: 'POST',
        body: { userId, file, requestId },
    });
}

export function removeLegalBoardCertificateDocument({ userId, termId, requestId }) {
    return fetchJson(`/legal-board/terms/${encodeURIComponent(termId)}/certificate-document`, {
        method: 'DELETE',
        body: { userId, requestId },
    });
}

export function fetchLegalBoardCertificateDocument({ userId, termId }) {
    return fetchJson(`/legal-board/terms/${encodeURIComponent(termId)}/certificate-document`, {
        query: { userId },
        responseType: 'blob',
    });
}

/** Fetch the stored certificate PDF and hand it to the browser as a download. */
export async function downloadLegalBoardCertificateDocument({ userId, termId, name }) {
    const blob = await fetchLegalBoardCertificateDocument({ userId, termId });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name || 'certificat.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Let the browser start reading the blob before releasing it.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function refreshLegalBoardStripe({ userId, termId }) {
    return fetchJson('/legal-board/stripe/refresh', {
        method: 'POST',
        body: { userId, termId },
    });
}
